import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import BillFlowDetail from '../../components/billflow/BillFlowDetail';
import StatusActionPanel from '../../components/billflow/StatusActionPanel';
import AskDialog from '../../components/common/dialog/AskDialog';
import * as taxbillApi from '../../lib/api/taxbill';
import * as billReplanApi from '../../lib/api/billReplan';

const BillFlowDetailContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [bill, setBill] = useState(null);
  const [pending, setPending] = useState(null); // { action, extra }
  // 처리 중 상태. 없으면 연타가 되고, 화면이 언제 갱신됐는지도 알 수 없어
  // "됐다 안 됐다" 처럼 보인다.
  const [submitting, setSubmitting] = useState(false);
  // 결재를 쓰는 설정이면 발행요청 단계가 살아있고, 아니면 [발행] 한 번으로 끝난다.
  const [approvalEnabled, setApprovalEnabled] = useState(false);
  // 발행 취소는 이미 나간 계산서를 되돌린다. 권한자만 가능하다.
  const [canCancelIssue, setCanCancelIssue] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await taxbillApi.getTaxbill(id);
      setBill(res.data?.info?.invInfo || null);
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    taxbillApi.getIssueFlowConfig()
      .then((res) => setApprovalEnabled(Boolean(res?.data?.approvalEnabled)))
      // 설정을 못 읽으면 결재 미사용으로 본다. 담당자가 직접 처리하는 쪽이 덜 막힌다.
      .catch(() => setApprovalEnabled(false));

    billReplanApi.getCancelIssuePermission()
      .then((res) => setCanCancelIssue(Boolean(res?.data?.allowed)))
      // 못 읽으면 막는 쪽으로 둔다. 되돌리기 어려운 작업이라 열어두는 것보다 안전하다.
      .catch(() => setCanCancelIssue(false));
  }, []);

  const onRequest = (action, extra) => setPending({ action, extra });
  const onClose = () => setPending(null);

  const onConfirm = async () => {
    if (!pending || submitting) return;
    const { action, extra } = pending;
    setSubmitting(true);
    try {
      const res = await taxbillApi.editTaxbill({
        invUuid: id,
        ...action.payload,
        ...extra,
      });

      // 서버가 바뀐 값을 그대로 돌려준다. 다시 조회하기 전에 먼저 반영해
      // 화면이 곧바로 다음 단계로 넘어가게 한다.
      const updated = res?.data?.info;
      if (updated) {
        setBill((prev) => ({ ...prev, ...updated }));
      }

      enqueueSnackbar(`${action.label} 완료`, { variant: 'success' });
      setPending(null);
      // 파생 값(수금 현황 등)까지 맞추기 위해 한 번 더 읽는다.
      await load();
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message || '처리에 실패했습니다.', { variant: 'error' });
      setPending(null);
    } finally {
      setSubmitting(false);
    }
  };

  const action = pending?.action;

  return (
    <>
      <BreadcrumbsContainer
        title="세금계산서 관리/세금계산서 흐름(new)/상세"
        icon="pay"
      />
      <Box sx={{ px: 2, py: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/billflow')}>
          목록
        </Button>
      </Box>
      {/* 수금이 등록되면 세금계산서의 수금완료 상태가 파생으로 바뀐다. 상세도 다시 읽는다. */}
      <BillFlowDetail bill={bill} onCollectChanged={load} />
      <Box sx={{ px: 2, pb: 2 }}>
        {bill && (
          <StatusActionPanel
            bill={bill}
            onSubmit={onRequest}
            submitting={submitting}
            approvalEnabled={approvalEnabled}
            canCancelIssue={canCancelIssue}
          />
        )}
      </Box>
      <AskDialog
        visible={!!pending}
        title={action ? action.label : ''}
        description={
          action
            ? action.cascade
              ? `"${action.label}" 처리 시 이후 단계까지 함께 초기화됩니다.\n계속하시겠습니까?`
              : `"${action.label}" 처리하시겠습니까?`
            : ''
        }
        confirmText={action?.kind === 'cancel' ? '취소 처리' : '처리'}
        cancelText="닫기"
        onConfirm={onConfirm}
        onCancle={onClose}
      />
    </>
  );
};

export default BillFlowDetailContainer;
